import { useRouter } from 'expo-router';
import { WifiOff, Lock, FileQuestion, AlertTriangle } from 'lucide-react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

export type ChatLoadErrorType = 'connection' | 'forbidden' | 'not_found' | 'unknown';

type ChatLoadErrorProps = {
  errorType: ChatLoadErrorType;
  onRetry?: () => void;
};

type ErrorConfig = {
  icon: typeof WifiOff;
  title: string;
  message: string;
  showRetry: boolean;
};

const errorConfigs: Record<ChatLoadErrorType, ErrorConfig> = {
  connection: {
    icon: WifiOff,
    title: 'Connection Error',
    message:
      'Unable to connect to the chat service. Please check your internet connection and try again.',
    showRetry: true,
  },
  forbidden: {
    icon: Lock,
    title: 'Access Denied',
    message: "You don't have permission to view this conversation.",
    showRetry: false,
  },
  not_found: {
    icon: FileQuestion,
    title: 'Conversation Not Found',
    message: "This conversation doesn't exist or may have been removed.",
    showRetry: false,
  },
  unknown: {
    icon: AlertTriangle,
    title: 'Unable to Load Chat',
    message: 'Something went wrong while loading this conversation. Please try again.',
    showRetry: true,
  },
};

export function ChatLoadError({ errorType, onRetry }: ChatLoadErrorProps) {
  const router = useRouter();

  const config = errorConfigs[errorType];

  const handleBack = () => {
    router.replace('/inbox');
  };

  return (
    <SafeAreaView
      className="flex-1 items-center justify-center bg-background-0 px-5 py-8"
      edges={['bottom']}>
      <VStack className="max-w-md items-center" space="lg">
        <Icon as={config.icon} size="xl" className="text-typography-400" />

        <VStack className="items-center" space="sm">
          <Heading size="h1-xl" className="text-center">
            {config.title}
          </Heading>

          <Text className="text-center text-typography-500">{config.message}</Text>
        </VStack>

        <HStack className="w-full" space="md">
          <Button variant="outline" className="flex-1" onPress={handleBack}>
            <ButtonText>Back to Inbox</ButtonText>
          </Button>

          {config.showRetry && onRetry && (
            <Button className="flex-1" onPress={onRetry}>
              <ButtonText>Try Again</ButtonText>
            </Button>
          )}
        </HStack>
      </VStack>
    </SafeAreaView>
  );
}
